type ApiRecord = Record<string, unknown>;

export interface NormalizedTokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  expires_in: number;
}

const isRecord = (value: unknown): value is ApiRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const unwrapApiData = <T = unknown>(value: unknown): T => {
  if (!isRecord(value)) {
    return value as T;
  }

  if ("data" in value) {
    return value.data as T;
  }

  if ("payload" in value) {
    return value.payload as T;
  }

  if ("result" in value) {
    return value.result as T;
  }

  return value as T;
};

export const asArray = <T>(value: unknown): T[] => {
  const body = unwrapApiData(value);

  if (Array.isArray(body)) {
    return body as T[];
  }

  if (isRecord(body) && Array.isArray(body.items)) {
    return body.items as T[];
  }

  return [];
};

export const extractTokenPair = (value: unknown): NormalizedTokenPair | null => {
  const body = unwrapApiData<ApiRecord>(value);
  const candidates: unknown[] = [];

  if (isRecord(body)) {
    candidates.push(body.tokens, body);
  }

  if (isRecord(value)) {
    candidates.push(value.tokens, value);
  }

  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;

    const accessToken = candidate.access_token;
    const refreshToken = candidate.refresh_token;

    if (typeof accessToken === "string" && accessToken.length > 0 && typeof refreshToken === "string" && refreshToken.length > 0) {
      return {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: candidate.token_type === "bearer" ? "bearer" : "bearer",
        expires_in: typeof candidate.expires_in === "number" ? candidate.expires_in : 0,
      };
    }
  }

  return null;
};

export const normalizeAuthEnvelope = <T extends object>(value: unknown, context: string): T => {
  const body = unwrapApiData<ApiRecord>(value);
  const tokens = extractTokenPair(value);

  if (!isRecord(body) || !tokens) {
    throw new Error(`${context}: përgjigjja nuk përmban token valid.`);
  }

  return {
    ...body,
    tokens,
  } as unknown as T;
};
